import { useCallback, useState } from 'react';

import type { CreateBulkIssuesBody, CreateBulkIssuesResponse } from '../__types__/apiUtils.ts';
import {
	ErrorStates,
	useBulkCreateContext,
	useSetError,
} from '../providers/BulkCreateContextProvider.tsx';
import { useBulkCreateLoggingEvents } from '../utils/useBulkCreateLoggingEvents.tsx';
import { updateIssuesWithRowIndex } from '../utils/tableUtils.tsx';

import { createBulkIssues } from './apiUtils.ts';

export const CREATE_BULK_ISSUES = 'create-bulk-issues';

export const useCreateBulkIssues = (): {
	createBulkIssuesHandler: ({
		issueBody,
		onComplete,
		onFailure,
		rowIndices,
	}: {
		issueBody: CreateBulkIssuesBody;
		onComplete: (response: CreateBulkIssuesResponse) => void;
		onFailure: (error: Error | unknown) => void;
		rowIndices?: number[];
	}) => Promise<void>;
	loading: boolean;
	response: CreateBulkIssuesResponse | null;
} => {
	const [{ currentJiraSite }] = useBulkCreateContext();

	const [loading, setLoading] = useState(false);
	const [response, setResponse] = useState<CreateBulkIssuesResponse | null>(null);
	const { logRequestFailed } = useBulkCreateLoggingEvents();
	const setError = useSetError();

	const createBulkIssuesHandler = useCallback(
		async ({
			issueBody,
			onComplete,
			onFailure,
			rowIndices,
		}: {
			issueBody: CreateBulkIssuesBody;
			onComplete: (response: CreateBulkIssuesResponse) => void;
			onFailure: (error: Error | unknown) => void;
			rowIndices?: number[];
		}): Promise<void> => {
			if (!currentJiraSite || !currentJiraSite.cloudId) {
				return;
			}
			setLoading(true);
			setResponse(null);
			try {
				const result = await createBulkIssues({ cloudId: currentJiraSite.cloudId, issueBody });
				if (!result) {
					onFailure('Empty response');
					logRequestFailed({
						requestName: CREATE_BULK_ISSUES,
						errorMessage: 'Failed to create issue: Empty from response call to bulk create',
					});
					void setError(ErrorStates.BULK_CREATION);
				} else {
					if (rowIndices && rowIndices.length > 0) {
						result.issues = updateIssuesWithRowIndex(result, rowIndices);
					}
					setResponse(result);
					onComplete(result);
				}
			} catch (err) {
				onFailure(err);
				logRequestFailed({
					requestName: CREATE_BULK_ISSUES,
					errorMessage: `Failed to create issue ${(err as Error).message}`,
				});
				void setError(ErrorStates.BULK_CREATION);
			} finally {
				setLoading(false);
			}
		},
		[currentJiraSite, logRequestFailed, setError],
	);

	return { createBulkIssuesHandler, loading, response };
};
